import { useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { OccurrenceCommentRow } from "./occurrenceCommentRow";
import { useOccurrenceComments } from "./useOccurrenceComments";

interface OccurrenceCommentsProps {
  occurrenceId: string;
  disabled: boolean | undefined;
}

const formatItemBadge = (count: number) =>
  `${String(count).padStart(2, "0")} ${count === 1 ? "item" : "itens"}`;

export const OccurrenceComments = ({ occurrenceId, disabled }: OccurrenceCommentsProps) => {
  const intl = useIntl();
  const panelContentRef = useRef<HTMLDivElement | null>(null);
  const [panelMeasuredHeight, setPanelMeasuredHeight] = useState(0);

  const {
    comments,
    itemCount,
    loading,
    expanded,
    setExpanded,
    newCommentText,
    setNewCommentText,
    submittingNew,
    editingCommentUuid,
    editDraft,
    setEditDraft,
    savingEditUuid,
    deletingUuid,
    submitNewComment,
    beginEdit,
    cancelEdit,
    saveEdit,
    removeComment,
    isCommentAuthor,
  } = useOccurrenceComments(occurrenceId);

  useLayoutEffect(() => {
    const el = panelContentRef.current;
    if (!el) return;

    const updateHeight = () => {
      setPanelMeasuredHeight(el.scrollHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [occurrenceId, expanded, loading, comments.length, editingCommentUuid]);

  return (
    <div className="border border-gray-300 rounded-md bg-white p-2">
      <button
        type="button"
        className="w-full flex flex-row items-center justify-between gap-3 px-4 py-3 text-left border-0 bg-transparent cursor-pointer"
        onClick={() => {
          if (disabled && comments.length === 0) return;
          setExpanded((v) => !v)
        }}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-size-14 font-medium text-color-dark mr-4">{intl.formatMessage({ id: "occurrence.comments.title" })}</span>
          <span className="inline-flex items-center rounded-full bg-[#98A2B3] text-size-14 text-color-white px-2 font-semibold">
            {formatItemBadge(itemCount)}
          </span>
        </div>
        <span
          className={`rioglyph rioglyph-chevron-down text-color-gray text-size-14 transition-transform duration-300 ease-in-out ${expanded ? "rotate-180" : "rotate-0"
            }`}
        />
      </button>

      <div
        className="transition-[max-height,opacity] duration-300 ease-in-out overflow-hidden"
        aria-hidden={!expanded}
        style={{
          maxHeight: expanded ? panelMeasuredHeight : 0,
          opacity: expanded ? 1 : 0,
          pointerEvents: expanded ? "auto" : "none",
        }}
      >
        <div ref={panelContentRef} className="px-4 pb-4 mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-color-dark">
              <div className="spinner mr-3">
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
              </div>
              {intl.formatMessage({ id: "occurrence.comments.loading" })}
            </div>
          ) : (
            <>
              {!disabled && (
                <div className={`pt-4 pb-6 ${comments.length > 0 ? "mb-6 border-b border-gray-300" : ""}`}>
                  <div className="text-size-14 lowercase text-color-status-resting mb-2">
                    {intl.formatMessage({ id: "occurrence.comments.submit" })}
                  </div>
                  <textarea
                    className="form-control w-full min-h-[120px] resize-y mb-3"
                    placeholder={intl.formatMessage({ id: "occurrence.comments.placeholder" })}
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    disabled={submittingNew}
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className={`btn btn-primary ${submittingNew ? "btn-loading disabled" : ""}`}
                      onClick={() => void submitNewComment()}
                      disabled={submittingNew || !newCommentText.trim()}
                    >
                      {intl.formatMessage({ id: "occurrence.comments.submit" })}
                    </button>
                  </div>
                </div>
              )}
              <div className="pt-2">
                {comments.map((comment) => (
                  <OccurrenceCommentRow
                    key={comment.uuid ?? `${comment.authorId}-${comment.commentedAt?.toString()}`}
                    comment={comment}
                    isAuthor={isCommentAuthor(comment)}
                    isEditing={editingCommentUuid === comment.uuid}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={() => beginEdit(comment)}
                    onCancelEdit={cancelEdit}
                    onSaveEdit={() => void saveEdit()}
                    onDelete={() => comment.uuid && void removeComment(comment.uuid)}
                    savingEdit={savingEditUuid === comment.uuid}
                    deleting={deletingUuid === comment.uuid}
                    disabled={disabled}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
